// ============================================================================
// OBT F3 — Müdür nihai park karar ekranı
// ============================================================================
// Müdür bir kampanya seçer; MY tarafından iletilen aday parkları görür ve nihai
// karar setini (dahil/çıkar/ertele) oluşturur, sonra KESİNLEŞTİRİR. Kesinleşen
// set salt-okunur olur; yeni karar için yeni SÜRÜM açılır. Backend optimistik
// version kullanmadığından 409/invalid-state durum-önkoşulu ihlalinden gelir.
//
// Müdür, bütçe/yönetim kararıyla aday kümesi DIŞINDAN bir parkı da nihai listeye
// ekleyebilir ("Park Ekle"). Bu park için Formen cevabı/BM incelemesi YOKTUR;
// kaynak `director_added` ile açıkça işaretlenir ve sahte cevap üretilmez.
// ============================================================================

import { useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '@/auth/AuthContext';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  ArrowLeft,
  ChevronRight,
  Gavel,
  Lock,
  Loader2,
  PlusCircle,
} from 'lucide-react';
import {
  ObtPageHeader,
  ObtLoading,
  ObtErrorState,
  ObtEmptyState,
} from '../ObtStates';
import { listDirectorQueue } from './reviewApi';
import { normalizeReviewError } from './reviewErrors';
import { DECISION_SET_STATUS_LABELS, labelFor } from './reviewEnums';
import { canFinalize } from './reviewValidation';
import { useFinalParkSelection } from './hooks/useFinalParkSelection';
import { useChildNames } from './hooks/useChildNames';
import FinalParkSelectionTable from './FinalParkSelectionTable';
import FinalDecisionDialog from './FinalDecisionDialog';
import DirectorAddParkDialog from './DirectorAddParkDialog';

// --------------------------------------------------------------------------
// Kampanya seçici — Müdür kuyruğunu kampanyaya göre grupla
// --------------------------------------------------------------------------

function useDirectorCampaigns(enabled) {
  const [state, setState] = useState({ loading: enabled, error: null, campaigns: [] });
  const abortRef = useRef(null);
  const load = useMemo(
    () => async () => {
      if (!enabled) {
        setState({ loading: false, error: null, campaigns: [] });
        return;
      }
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      setState((prev) => ({ ...prev, loading: true, error: null }));
      try {
        const items = await listDirectorQueue({}, { signal: controller.signal });
        if (controller.signal.aborted) return;
        const groups = new Map();
        for (const it of items) {
          const key = String(it.campaign_id);
          if (!groups.has(key)) {
            groups.set(key, {
              campaign_id: it.campaign_id,
              campaign_title: it.campaign_title || 'Bilgi Talebi',
              campaign_topic: it.campaign_topic || null,
              candidate_count: 0,
            });
          }
          groups.get(key).candidate_count += 1;
        }
        setState({
          loading: false,
          error: null,
          campaigns: Array.from(groups.values()).sort((a, b) =>
            String(a.campaign_title).localeCompare(String(b.campaign_title), 'tr')
          ),
        });
      } catch (error) {
        if (
          controller.signal.aborted ||
          error?.code === 'ERR_CANCELED' ||
          error?.name === 'CanceledError'
        ) {
          return;
        }
        setState({ loading: false, error: normalizeReviewError(error), campaigns: [] });
      }
    },
    [enabled]
  );
  useEffect(() => {
    load();
    return () => abortRef.current?.abort();
  }, [load]);
  return { ...state, reload: load };
}

function CampaignPicker({ enabled, onPick }) {
  const { loading, error, campaigns, reload } = useDirectorCampaigns(enabled);
  if (loading) return <ObtLoading label="Bilgi Talepleri yükleniyor…" />;
  if (error) return <ObtErrorState error={error} onRetry={reload} />;
  if (campaigns.length === 0) {
    return (
      <ObtEmptyState
        title="Karar bekleyen bilgi talebi yok"
        description="Nihai kararınızı bekleyen (MY onaylı cevap içeren) bilgi talebi bulunmuyor."
      />
    );
  }
  return (
    <div className="space-y-2">
      {campaigns.map((c) => (
        <Card key={String(c.campaign_id)}>
          <CardContent className="flex items-center justify-between gap-2 p-4">
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <h4 className="text-sm font-semibold text-gray-900">
                  {c.campaign_title}
                </h4>
                {c.campaign_topic && (
                  <span className="rounded bg-gray-100 px-2 py-0.5 text-xs text-gray-600">
                    {c.campaign_topic}
                  </span>
                )}
              </div>
              <p className="mt-1 text-xs text-gray-500">
                {c.candidate_count} aday cevap
              </p>
            </div>
            <Button size="sm" onClick={() => onPick(c)}>
              Kararı Aç
              <ChevronRight className="ml-1 h-4 w-4" aria-hidden="true" />
            </Button>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

// --------------------------------------------------------------------------
// Seçili kampanya karar çalışma alanı
// --------------------------------------------------------------------------

function CampaignDecisionWorkspace({ campaign, enabled, onBack }) {
  const { user } = useAuth();
  const nameById = useChildNames(user?.personnelId || null, enabled);
  const sel = useFinalParkSelection(campaign.campaign_id, { enabled });
  const [pending, setPending] = useState(null); // {responseId, decision, parkName, explanation}
  const [finalizeOpen, setFinalizeOpen] = useState(false);
  const [finalizing, setFinalizing] = useState(false);
  const [decisionText, setDecisionText] = useState('');
  const [addOpen, setAddOpen] = useState(false);

  const { decisionSet } = sel;
  const isDraft = decisionSet?.status === 'draft';
  const isFinalized = decisionSet?.status === 'finalized';
  const finalizeReady = useMemo(
    () => canFinalize(sel.candidates, decisionSet?.items),
    [sel.candidates, decisionSet]
  );

  const handleFinalize = async () => {
    setFinalizing(true);
    try {
      await sel.finalize(decisionText.trim() || null);
      setFinalizeOpen(false);
      setDecisionText('');
    } catch {
      /* hata sel.actionError ile gösterilir */
    } finally {
      setFinalizing(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="sm" onClick={onBack}>
          <ArrowLeft className="mr-2 h-4 w-4" aria-hidden="true" /> Bilgi Talepleri
        </Button>
        <h3 className="truncate text-base font-semibold text-gray-900">
          {campaign.campaign_title}
        </h3>
        {decisionSet && (
          <span className="ml-auto inline-flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-semibold text-slate-700">
            {`Sürüm ${decisionSet.version_number} · ${labelFor(
              DECISION_SET_STATUS_LABELS,
              decisionSet.status
            )}`}
          </span>
        )}
      </div>

      {sel.loading && <ObtLoading label="Karar seti yükleniyor…" />}
      {!sel.loading && sel.error && (
        <ObtErrorState error={sel.error} onRetry={sel.reload} />
      )}

      {!sel.loading && !sel.error && (
        <>
          {sel.actionError && (
            <p role="alert" className="text-sm text-red-600">
              {sel.actionError.message}
            </p>
          )}

          {/* Taslak yoksa: başlat (ilk kez) veya yeni sürüm (kesinleşmiş sonrası) */}
          {!isDraft && (
            <Card>
              <CardContent className="flex flex-col items-start gap-3 p-4">
                {isFinalized ? (
                  <>
                    <p className="flex items-center gap-2 text-sm text-gray-700">
                      <Lock className="h-4 w-4 text-gray-500" aria-hidden="true" />
                      Bu bilgi talebinin nihai kararı kesinleştirilmiştir (salt-okunur).
                    </p>
                    <Button onClick={() => sel.revise()} disabled={sel.actionPending}>
                      <PlusCircle className="mr-2 h-4 w-4" aria-hidden="true" /> Yeni Karar
                      Sürümü Aç
                    </Button>
                  </>
                ) : (
                  <>
                    <p className="text-sm text-gray-700">
                      Bu bilgi talebi için henüz bir karar taslağı yok. Kararları girmek
                      için taslağı başlatın.
                    </p>
                    <Button onClick={() => sel.createDraft()} disabled={sel.actionPending}>
                      <Gavel className="mr-2 h-4 w-4" aria-hidden="true" /> Karar Setini
                      Başlat
                    </Button>
                  </>
                )}
              </CardContent>
            </Card>
          )}

          {isDraft && (
            <div className="flex items-center justify-end">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setAddOpen(true)}
                disabled={sel.actionPending}
              >
                <PlusCircle className="mr-2 h-4 w-4" aria-hidden="true" /> Park Ekle
              </Button>
            </div>
          )}

          <FinalParkSelectionTable
            candidates={sel.candidates}
            items={decisionSet?.items}
            readOnly={!isDraft}
            nameById={nameById}
            actionPending={sel.actionPending}
            onDecide={(responseId, decision, parkName, explanation) =>
              setPending({ responseId, decision, parkName, explanation })
            }
            onRemoveDirectorPark={(itemId) => sel.removeDirectorParkItem(itemId)}
          />

          {isDraft && (
            <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-gray-200 bg-gray-50 p-3">
              <p className="text-sm text-gray-600">
                {finalizeReady
                  ? 'Tüm adaylar karara bağlandı. Nihai listeyi kesinleştirebilirsiniz.'
                  : 'Kesinleştirmeden önce tüm aday parkları karara bağlayın.'}
              </p>
              <Button
                onClick={() => setFinalizeOpen(true)}
                disabled={!finalizeReady || sel.actionPending}
              >
                <Lock className="mr-2 h-4 w-4" aria-hidden="true" /> Kesinleştir
              </Button>
            </div>
          )}
        </>
      )}

      {/* Karar gerekçe diyaloğu */}
      <FinalDecisionDialog
        pending={pending}
        onClose={() => setPending(null)}
        error={sel.actionError?.message || null}
        onConfirm={async (explanation) => {
          await sel.setDecision(pending.responseId, pending.decision, explanation);
          setPending(null);
        }}
      />

      {/* Müdür "Park Ekle" diyaloğu — aday kümesi dışından park */}
      <DirectorAddParkDialog
        open={addOpen}
        decisionSetId={sel.draftId}
        error={sel.actionError?.message || null}
        onClose={() => setAddOpen(false)}
        onAdd={async (parkId, reason) => {
          await sel.addDirectorParkItem(parkId, reason);
        }}
      />

      {/* Kesinleştirme onayı */}
      <Dialog open={finalizeOpen} onOpenChange={(v) => !finalizing && setFinalizeOpen(v)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nihai Listeyi Kesinleştir</DialogTitle>
            <DialogDescription>
              Nihai park listesi kesinleştirilecektir. Kesinleştirme sonrasında
              kararlar salt-okunur olur.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5">
            <Label htmlFor="finalize-note">Karar açıklaması (opsiyonel)</Label>
            <Textarea
              id="finalize-note"
              value={decisionText}
              maxLength={8000}
              rows={3}
              placeholder="Nihai karara ilişkin genel açıklama…"
              onChange={(e) => setDecisionText(e.target.value)}
              disabled={finalizing}
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setFinalizeOpen(false)}
              disabled={finalizing}
            >
              Vazgeç
            </Button>
            <Button onClick={handleFinalize} disabled={finalizing}>
              {finalizing && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
              )}
              Kesinleştir
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/**
 * @param {object} props
 * @param {boolean} [props.enabled]
 * @param {() => void} [props.onBack]
 */
export default function DirectorFinalDecisionScreen({ enabled = true, onBack }) {
  const [campaign, setCampaign] = useState(null);

  return (
    <div className="space-y-4">
      <ObtPageHeader
        onBack={!campaign ? onBack : null}
        title="Nihai Park Kararı"
        description="MY tarafından iletilen aday parkları değerlendirin ve nihai listeyi kesinleştirin."
      />
      {!campaign ? (
        <CampaignPicker enabled={enabled} onPick={setCampaign} />
      ) : (
        <CampaignDecisionWorkspace
          campaign={campaign}
          enabled={enabled}
          onBack={() => setCampaign(null)}
        />
      )}
    </div>
  );
}
